import { Box, Container, Divider, Grid, IconButton, makeStyles, Theme, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { CarouselComponent, ContentDescription } from 'components/structures/general';
import { Arrows } from 'components/structures/main/Slide';
import { MapCardProps, mapCardsData } from 'data/map-data';
import { useMediaUp } from 'hooks';
import { FC, useRef, useState, useEffect } from 'react';
import Iframe from 'react-iframe';
import { ResponsiveType } from 'react-multi-carousel';
import { CSSTransition } from 'react-transition-group';
import { EmailMapIcon, MarkerMapIcon, PhoneMapIcon, WhitePlusIcon } from 'svgs';

const responsive = (single: boolean): ResponsiveType => ({
  mobile: {
    breakpoint: { max: 960, min: 0 },
    items: 1,
  },
  tablet: {
    breakpoint: { max: 1280, min: 960 },
    items: single ? 1 : 2,
  },
  desktop: {
    breakpoint: { max: 5000, min: 1280 },
    items: single ? 1 : 3,
  },
});

const useStyles = makeStyles((theme: Theme) => ({
  container: { marginBottom: theme.spacing(20) },
  googleMap: { borderRadius: theme.spacing(4) },
  showMoreBtn: {
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(3),
    position: 'absolute',
    top: theme.spacing(5),
    left: theme.spacing(2),
    transition: 'all 400ms',
  },
  closeButton: {
    '& svg': { transform: 'rotate(45deg)' },
  },
  linkDescription: { '&:hover': { textDecoration: 'underline' } },
  imageCard: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    borderRadius: theme.spacing(4),
    boxShadow: '0px 33px 18px -17px rgba(0, 0, 0, 0.15)',
    userSelect: 'none',
    transition: 'width 200ms',
    maxHeight: 400,
  },
  link: { margin: theme.spacing(3, 0), '&:hover': { textDecoration: 'underline' } },
  iconMapInfo: { margin: theme.spacing(6, 0, 3) },
  imageButton: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    width: '100%',
    paddingBottom: theme.spacing(6),
  },
  arrowWrapper: { margin: theme.spacing(-8, 0, 8), [theme.breakpoints.up('md')]: { margin: theme.spacing(8, 0, 14) } },
  fadeInCarouselAnime: {
    animation: '$fadeInCarousel 400ms forwards',
    pointerEvents: 'auto',
    animationDelay: '200ms',
    opacity: 0,
    maxHeight: 0,
  },
  fadeOutCarouselAnime: { animation: '$fadeOutCarousel 200ms forwards', pointerEvents: 'none' },
  fadeInInfoAnime: {
    animation: '$fadeInInfo 600ms forwards',
    pointerEvents: 'auto',
    animationDelay: '200ms',
    opacity: 0,
  },
  fadeOutInfoAnime: { animation: '$fadeOutInfo 200ms forwards', pointerEvents: 'none' },
  '@keyframes fadeInCarousel': {
    from: { opacity: 0, transform: 'scale(1.3)', maxHeight: 0 },
    to: { opacity: 1, transform: 'scale(1)', maxHeight: '100%' },
  },
  '@keyframes fadeOutCarousel': {
    from: { opacity: 1, transform: 'scale(1)', maxHeight: '100%' },
    to: { opacity: 0, transform: 'scale(1.3)', maxHeight: 0 },
  },
  '@keyframes fadeInInfo': {
    from: { opacity: 0, transform: 'scale(0)', maxHeight: 0 },
    to: { opacity: 1, transform: 'scale(1)', maxHeight: '100%' },
  },
  '@keyframes fadeOutInfo': {
    from: { opacity: 1, transform: 'scale(1)', maxHeight: '100%' },
    to: { opacity: 0, transform: 'scale(0.7)', maxHeight: 0 },
  },
}));

const MapSlider: FC = () => {
  const classes = useStyles();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedLocation, setSelectedLocation] = useState<null | string>(null);
  const [loading, setLoading] = useState(false);

  const sliderRef = useRef<any>(null);
  const infoRef = useRef<any>(null);

  const prev = () => {
    if (!loading) {
      infoRef.current?.previous();
      sliderRef.current?.previous();
      setCurrentSlide(currentSlide === 0 ? mapCardsData.length - 1 : currentSlide - 1);
    }
  };

  const next = () => {
    if (!loading) {
      infoRef.current?.next();
      sliderRef.current?.next();
      setCurrentSlide(currentSlide >= mapCardsData.length - 1 ? 0 : currentSlide + 1);
    }
  };

  const carouselProps = {
    beforeChange: () => setLoading(true),
    afterChange: () => setLoading(false),
    swipeable: false,
  };

  const slidesLength: number[] = mapCardsData.map((_, index) => index);

  useEffect(() => {
    if (selectedLocation) {
      const newIndex = mapCardsData.findIndex(({ title }) => title === selectedLocation);
      setCurrentSlide(newIndex);
      infoRef.current?.goToSlide(newIndex + 6);
    }
  }, [selectedLocation, infoRef]);

  return (
    <Container fixed className={classes.container}>
      <ContentDescription title={{ text: 'You may find us here:' }} upperTitle={{ text: 'Our offices' }} />
      <Box className={classes.arrowWrapper}>
        <Arrows currentSlide={currentSlide} totalSlides={slidesLength} next={next} prev={prev} />
      </Box>
      <CSSTransition
        in={!selectedLocation}
        timeout={370}
        classNames={{ enter: classes.fadeInCarouselAnime, exit: classes.fadeOutCarouselAnime }}
        unmountOnExit
      >
        <CarouselComponent {...carouselProps} responsive={responsive(false)} ref={sliderRef}>
          {mapCardsData.map((item, index) => (
            <MapCard setSelectedLocation={setSelectedLocation} {...item} key={index} />
          ))}
        </CarouselComponent>
      </CSSTransition>
      <CSSTransition
        in={!!selectedLocation}
        timeout={370}
        classNames={{ enter: classes.fadeInInfoAnime, exit: classes.fadeOutInfoAnime }}
        unmountOnExit
        onEntered={() => {
          if (selectedLocation) {
            const newIndex = mapCardsData.findIndex(({ title }) => title === selectedLocation);
            if (newIndex !== -1) {
              setCurrentSlide(newIndex);
              infoRef.current?.goToSlide(newIndex);
            }
          }
        }}
      >
        <CarouselComponent {...carouselProps} responsive={responsive(true)} ref={infoRef}>
          {mapCardsData.map((item, index) => (
            <MapInfo setSelectedLocation={setSelectedLocation} {...item} key={index} />
          ))}
        </CarouselComponent>
      </CSSTransition>
    </Container>
  );
};

const MapCard: FC<MapCardProps> = ({
  title,
  setSelectedLocation,
  description,
  googleMapLink,
  mapImage: { origin, origin2x },
}) => {
  const classes = useStyles();
  const handleClick = () => setSelectedLocation(title);
  return (
    <Box display="flex" justifyContent="center">
      <Box maxWidth={415} px={3} position="relative">
        <IconButton onClick={handleClick} color="primary" className={classes.showMoreBtn}>
          <WhitePlusIcon />
        </IconButton>
        <button onClick={handleClick} className={classes.imageButton}>
          <img className={classes.imageCard} src={origin} alt={title} srcSet={`${origin} 578w, ${origin2x} 1156w`} />
        </button>
        <Box mb={3}>
          <Typography variant="h3">{title}</Typography>
        </Box>
        <Typography
          variant="body2"
          component="a"
          target="_blank"
          rel="noopener noreferrer"
          href={googleMapLink}
          className={classes.linkDescription}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

const MapInfo: FC<MapCardProps> = ({ title, setSelectedLocation, iframeLink, description, googleMapLink, email, phone }) => {
  const classes = useStyles();
  const handleClick = () => setSelectedLocation(null);
  const isMediumUp = useMediaUp('md');
  return (
    <Grid spacing={6} container>
      <Grid lg={9} md={8} xs={12} item>
        <Box position="relative" px={7}>
          <IconButton onClick={handleClick} color="primary" className={clsx(classes.showMoreBtn, classes.closeButton)}>
            <WhitePlusIcon />
          </IconButton>
          <Iframe
            width="100%"
            height={isMediumUp ? '700' : '330'}
            frameBorder={0}
            scrolling="no"
            className={classes.googleMap}
            url={iframeLink}
            loading="lazy"
          />
        </Box>
      </Grid>
      <Grid lg={3} md={4} xs={12} item>
        <Typography variant="h3">{title}</Typography>
        <Box mt={isMediumUp ? 8 : 2}>
          <MarkerMapIcon className={classes.iconMapInfo} />
          <Typography variant="h4">Address</Typography>
          <Box className={classes.link}>
            <Typography target="_blank" rel="noopener noreferrer" href={googleMapLink} component="a">
              {description}
            </Typography>
          </Box>
          <Divider />
          <EmailMapIcon className={classes.iconMapInfo} />
          <Typography variant="h4">Email</Typography>
          <Box className={classes.link}>
            <Typography href={'mailto:' + email} component="a">
              {email}
            </Typography>
          </Box>
          {!!phone && (
            <>
              <Divider />
              <PhoneMapIcon className={classes.iconMapInfo} />
              <Typography variant="h4">Phone</Typography>
              <Box className={classes.link}>
                <Typography href={'tel:' + phone} component="a">
                  {phone}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default MapSlider;
